import type { AnalyticsGoal, CollectItem } from '../../shared/analytics';
import { analyticsLimits } from '../../shared/analytics';
import { captureFirstTouch, firstTouch } from './attribution';
import { adminBasePath } from '../adminPath';

/**
 * First-party analytics tracker. Everything here touches `window`/`document` only inside functions
 * called from effects or event handlers, so importing it during prerendering is safe.
 *
 * Without consent only anonymous pageviews (and the `lead_submit` count) are sent: no cookies are
 * written, no visitor/session ids, no clicks, scroll or time on page.
 */
export type Consent = 'accepted' | 'declined';
type ConsentListener = (consent: Consent | null, reopened: boolean) => void;
type PageviewItem = Extract<CollectItem, { type: 'pageview' }>;
type Pageview = {
  id: string;
  path: string;
  startedAt: number;
  activeMs: number;
  /** `performance.now()` when the tab became visible, `null` while hidden. */
  activeSince: number | null;
  scrollPct: number;
  /** Set once the hit has been built (after the route's title effect ran). */
  item: PageviewItem | null;
  /** Whether the server knows this view as part of a consented session. */
  consented: boolean;
};

export const consentCookie = 'iz_consent';
export const visitorCookie = 'iz_vid';
export const sessionCookie = 'iz_sid';
const yearSeconds = 365 * 24 * 60 * 60;
/** Sliding visit timeout, as in Yandex Metrica. */
const sessionSeconds = 30 * 60;
const clickFlushMs = 5000;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const interactiveSelector = 'a[href], button, input, select, textarea, label, summary, [role="button"], [role="link"], [role="tab"], [tabindex]:not([tabindex="-1"])';

let current: Pageview | null = null;
let previousPath: string | null = null;
let landingUsed = false;
let queue: CollectItem[] = [];
let flushTimer: number | undefined;
let sessionTouchedAt = 0;
let scrollFrame = 0;
const consentListeners = new Set<ConsentListener>();

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const separator = part.indexOf('=');
    if (part.slice(0, separator).trim() === name) return decodeURIComponent(part.slice(separator + 1).trim());
  }
  return null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`;
}

/** crypto.randomUUID needs a secure context; plain-http dev servers fall back to getRandomValues. */
function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Never track prerendering, the admin click-map iframe, automation, or the staff area (ADMIN_PATH, known only in the admin shell). */
export function trackingAllowed(path?: string): boolean {
  if (typeof window === 'undefined' || window.top !== window.self || navigator.webdriver) return false;
  const base = adminBasePath();
  const current = path ?? window.location.pathname;
  return !base || (current !== base && !current.startsWith(`${base}/`));
}

export function getConsent(): Consent | null {
  if (typeof document === 'undefined') return null;
  const value = readCookie(consentCookie);
  return value === 'accepted' || value === 'declined' ? value : null;
}

export function subscribeConsent(listener: ConsentListener): () => void {
  consentListeners.add(listener);
  return () => { consentListeners.delete(listener); };
}

/** Ids for a consented hit; refreshes the 1-year visitor cookie and the 30-minute session cookie. */
function identity(): { visitorId: string; sessionId: string } | null {
  if (getConsent() !== 'accepted') return null;
  const storedVisitor = readCookie(visitorCookie);
  const storedSession = readCookie(sessionCookie);
  const visitorId = storedVisitor && uuidPattern.test(storedVisitor) ? storedVisitor : uuid();
  const sessionId = storedSession && uuidPattern.test(storedSession) ? storedSession : uuid();
  writeCookie(visitorCookie, visitorId, yearSeconds);
  writeCookie(sessionCookie, sessionId, sessionSeconds);
  sessionTouchedAt = Date.now();
  return { visitorId, sessionId };
}

function send(body: { visitorId?: string; sessionId?: string; items: CollectItem[] }) {
  // keepalive (not sendBeacon): the request must carry Origin for the server's origin guard.
  void fetch('/api/metrics/collect', { method: 'POST', keepalive: true, credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => undefined);
}

function flush() {
  window.clearTimeout(flushTimer);
  flushTimer = undefined;
  if (!queue.length) return;
  const ids = identity();
  const items = queue;
  queue = [];
  if (!ids) return;
  for (let index = 0; index < items.length; index += analyticsLimits.items) send({ ...ids, items: items.slice(index, index + analyticsLimits.items) });
}

function scheduleFlush() {
  flushTimer ??= window.setTimeout(flush, clickFlushMs);
}

function activeTime(pageview: Pageview): number {
  return pageview.activeMs + (pageview.activeSince === null ? 0 : performance.now() - pageview.activeSince);
}

function pause(pageview: Pageview) {
  pageview.activeMs = activeTime(pageview);
  pageview.activeSince = null;
}

function queueEngagement(pageview: Pageview) {
  if (!pageview.consented) return;
  queue.push({ type: 'engagement', pageviewId: pageview.id, timeMs: Math.min(86_400_000, Math.round(activeTime(pageview))), scrollPct: pageview.scrollPct });
}

function measureScroll() {
  scrollFrame = 0;
  if (!current) return;
  const height = document.documentElement.scrollHeight;
  const seen = height > 0 ? Math.round(((window.scrollY + window.innerHeight) / height) * 100) : 100;
  current.scrollPct = Math.max(current.scrollPct, Math.min(100, Math.max(0, seen)));
}

/** Landing-hit context: external referrer (origin + path only) and campaign markers from the URL. */
function landingContext(): Pick<PageviewItem, 'referrer' | 'utm' | 'adClick'> {
  const params = new URLSearchParams(window.location.search);
  const utm: NonNullable<PageviewItem['utm']> = {};
  for (const key of ['source', 'medium', 'campaign', 'term', 'content'] as const) {
    const value = params.get(`utm_${key}`)?.trim().slice(0, analyticsLimits.utm);
    if (value) utm[key] = value;
  }
  const adClick = params.has('yclid') ? 'yclid' : params.has('gclid') ? 'gclid' : undefined;
  let referrer: string | undefined;
  try {
    const url = document.referrer ? new URL(document.referrer) : null;
    if (url) referrer = url.origin === window.location.origin ? url.pathname : `${url.origin}${url.pathname}`.slice(0, analyticsLimits.referrer);
  } catch { /* opaque referrer */ }
  return { ...(referrer ? { referrer } : {}), ...(Object.keys(utm).length ? { utm } : {}), ...(adClick ? { adClick } : {}) };
}

function sendPageview(pageview: Pageview) {
  if (!pageview.item) return;
  if (getConsent() === 'accepted') {
    pageview.consented = true;
    // A visit that starts after the landing hit (cookies accepted on a later page, or a new visit in the
    // same tab) keeps the tab's campaign, so tracked links count their visits.
    const touch = !pageview.item.utm && !readCookie(sessionCookie) ? firstTouch() : null;
    queue.push(touch ? { ...pageview.item, utm: touch } : pageview.item);
    flush();
  } else {
    // Anonymous hits carry only what the per-row source/device breakdown needs.
    const { screen: _screen, viewport: _viewport, language: _language, timezone: _timezone, ...anonymous } = pageview.item;
    send({ items: [anonymous] });
  }
}

function finishPageview() {
  if (!current) return;
  pause(current);
  queueEngagement(current);
  current = null;
}

/** Records a view of `path`; called on every route change. */
export function trackPageview(path: string) {
  if (!trackingAllowed(path)) return;
  // StrictMode re-runs effects with the same location; one view per navigation.
  if (current?.path === path && performance.now() - current.startedAt < 1500) return;
  finishPageview();
  const referrer = previousPath ?? undefined;
  if (!landingUsed) captureFirstTouch();
  const context = landingUsed ? (referrer ? { referrer } : {}) : landingContext();
  landingUsed = true;
  previousPath = path;
  const pageview: Pageview = { id: uuid(), path, startedAt: performance.now(), activeMs: 0, activeSince: document.visibilityState === 'visible' ? performance.now() : null, scrollPct: 0, item: null, consented: false };
  current = pageview;
  // The route's head effect sets document.title in the same commit; read it after effects flushed.
  setTimeout(() => {
    pageview.item = {
      type: 'pageview', id: pageview.id, path: path.slice(0, analyticsLimits.path), title: document.title.slice(0, analyticsLimits.title), ...context,
      screen: `${window.screen.width}x${window.screen.height}`, viewport: window.innerWidth,
      language: navigator.language.slice(0, 35), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 64),
    };
    if (current === pageview) measureScroll();
    sendPageview(pageview);
  }, 0);
}

/** Sends a goal. With consent it joins the visit; without consent only `lead_submit` is counted, anonymously. */
export function trackGoal(name: AnalyticsGoal, href?: string) {
  if (!trackingAllowed()) return;
  const path = current?.path ?? window.location.pathname;
  if (getConsent() === 'accepted' && current?.consented) {
    queue.push({ type: 'goal', name, path, pageviewId: current.id, ...(href ? { href: href.slice(0, analyticsLimits.href) } : {}) });
    flush();
  } else if (name === 'lead_submit') {
    send({ items: [{ type: 'goal', name, path }] });
  }
}

function linkGoal(link: HTMLAnchorElement): AnalyticsGoal | null {
  if (link.protocol === 'tel:') return 'phone_click';
  if (link.protocol !== 'http:' && link.protocol !== 'https:') return null;
  const host = link.hostname.toLowerCase();
  if (host === 't.me' || host === 'telegram.me' || host.endsWith('.t.me')) return 'telegram_click';
  if (host === 'max.ru' || host.endsWith('.max.ru')) return 'max_click';
  if (link.host === window.location.host) return link.pathname.startsWith('/documents/') ? 'document_download' : null;
  return 'outbound_click';
}

/** Stable-ish CSS selector: nearest usable id or data attribute, else a `tag:nth-of-type` chain up to 8 levels. */
function selectorFor(element: Element): string {
  const parts: string[] = [];
  let node: Element | null = element;
  for (let depth = 0; node && depth < 8; depth += 1) {
    if (node === document.body) { parts.unshift('body'); break; }
    if (node.id && /^[A-Za-z][\w-]*$/.test(node.id) && document.querySelectorAll(`#${node.id}`).length === 1) { parts.unshift(`#${node.id}`); break; }
    const tag = node.localName;
    const dataAttribute = ['data-analytics', 'data-testid', 'data-section'].find((name) => node?.getAttribute(name));
    if (dataAttribute) {
      const value = node.getAttribute(dataAttribute) ?? '';
      if (/^[\w-]{1,60}$/.test(value)) { parts.unshift(`${tag}[${dataAttribute}="${value}"]`); break; }
    }
    let index = 1;
    for (let sibling = node.previousElementSibling; sibling; sibling = sibling.previousElementSibling) if (sibling.localName === tag) index += 1;
    parts.unshift(`${tag}:nth-of-type(${index})`);
    node = node.parentElement;
  }
  while (parts.length > 1 && parts.join(' > ').length > analyticsLimits.selector) parts.shift();
  return parts.join(' > ').slice(0, analyticsLimits.selector);
}

function onClick(event: MouseEvent) {
  if (!current || !(event.target instanceof Element)) return;
  const link = event.target.closest('a[href]');
  if (link instanceof HTMLAnchorElement) {
    const goal = linkGoal(link);
    if (goal) trackGoal(goal, link.href);
  }
  // Keyboard activation (detail 0) has no meaningful coordinates.
  if (!current.consented || getConsent() !== 'accepted' || event.detail === 0) return;
  const element = event.target.closest(interactiveSelector) ?? event.target.closest('svg') ?? event.target;
  const rect = element.getBoundingClientRect();
  const ratio = (offset: number, size: number) => (size > 0 ? Math.round(Math.min(1, Math.max(0, offset / size)) * 1000) / 1000 : 0.5);
  const clamp = (value: number) => Math.min(100_000, Math.max(0, Math.round(value)));
  const textSource = /^(?:a|button|label|summary)$/.test(element.localName) || element.getAttribute('role') === 'button';
  const text = textSource ? (element.getAttribute('aria-label') || element.textContent || '').replace(/\s+/g, ' ').trim().slice(0, analyticsLimits.text) : '';
  const href = element instanceof HTMLAnchorElement ? element.href.slice(0, analyticsLimits.href) : '';
  queue.push({
    type: 'click', pageviewId: current.id, path: current.path, selector: selectorFor(element),
    ex: ratio(event.clientX - rect.left, rect.width), ey: ratio(event.clientY - rect.top, rect.height),
    px: clamp(event.pageX), py: clamp(event.pageY),
    dw: clamp(document.documentElement.scrollWidth), dh: clamp(document.documentElement.scrollHeight), vw: clamp(window.innerWidth),
    ...(href ? { href } : {}), ...(text ? { text } : {}),
  });
  scheduleFlush();
}

function onActivity() {
  if (scrollFrame === 0 && current) scrollFrame = requestAnimationFrame(measureScroll);
  // Interaction keeps the 30-minute visit alive even without new hits.
  if (getConsent() === 'accepted' && Date.now() - sessionTouchedAt > 60_000 && readCookie(sessionCookie)) {
    sessionTouchedAt = Date.now();
    writeCookie(sessionCookie, readCookie(sessionCookie) ?? '', sessionSeconds);
  }
}

function onHide() {
  if (!current) return;
  pause(current);
  queueEngagement(current);
  flush();
}

function onVisibilityChange() {
  if (!current) return;
  if (document.visibilityState === 'hidden') { onHide(); return; }
  // Returning after the visit timed out starts a new visit, as Metrica does.
  if (current.consented && getConsent() === 'accepted' && !readCookie(sessionCookie)) {
    const { path } = current;
    current = null;
    trackPageview(path);
    return;
  }
  current.activeSince ??= performance.now();
}

/** Installs the document listeners; returns the cleanup. */
export function startTracking(): () => void {
  if (!trackingAllowed()) return () => undefined;
  document.addEventListener('click', onClick, { capture: true, passive: true });
  document.addEventListener('visibilitychange', onVisibilityChange);
  document.addEventListener('keydown', onActivity, { passive: true });
  window.addEventListener('scroll', onActivity, { passive: true });
  window.addEventListener('pagehide', onHide);
  if (current && document.visibilityState === 'visible') current.activeSince ??= performance.now();
  return () => {
    document.removeEventListener('click', onClick, { capture: true });
    document.removeEventListener('visibilitychange', onVisibilityChange);
    document.removeEventListener('keydown', onActivity);
    window.removeEventListener('scroll', onActivity);
    window.removeEventListener('pagehide', onHide);
    cancelAnimationFrame(scrollFrame);
    scrollFrame = 0;
    onHide();
  };
}

function notify(consent: Consent | null, reopened: boolean) {
  for (const listener of consentListeners) listener(consent, reopened);
}

export function setConsent(consent: Consent) {
  writeCookie(consentCookie, consent, yearSeconds);
  if (consent === 'declined') {
    queue = [];
    writeCookie(visitorCookie, '', 0);
    writeCookie(sessionCookie, '', 0);
  } else if (current?.item && !current.consented && trackingAllowed()) {
    // The anonymous hit for this page joins the new visit server-side instead of being counted twice.
    sendPageview(current);
  }
  notify(consent, false);
}

/** Withdraws the choice (privacy page «Настройки cookie»): flushes pending data, deletes all analytics cookies and re-shows the banner. */
export function resetConsent() {
  if (current?.consented) {
    pause(current);
    queueEngagement(current);
    flush();
    // A fresh id: the old one already belongs to the previous visit.
    current.id = uuid();
    current.consented = false;
    if (current.item) current.item = { ...current.item, id: current.id };
    // The new view starts counting from zero; time and scroll of the old one were already reported.
    current.activeMs = 0;
    current.scrollPct = 0;
    current.activeSince = document.visibilityState === 'visible' ? performance.now() : null;
  }
  queue = [];
  for (const name of [consentCookie, visitorCookie, sessionCookie]) writeCookie(name, '', 0);
  notify(null, true);
}
